import ZoomVideoSDK, { ExecutedFailure, RecordingStatus } from "@zoom/videosdk";
import { secondsToMilliseconds } from "date-fns";
import { LocalParticipant as TwilioLocalParticipant } from "twilio-video";
import { logger } from "../../datadog/logger";
import { JotaiStore } from "../../state/types";
import {
  normalizeError,
  wait,
  withRetry,
  withTemporaryState,
  withTimeout,
} from "../../utils";
import type { VideoSDKAdapter } from "../core/adapter";
import { currentOutputDeviceIdAtom, videoSDKAtom } from "../core/atoms";
import { SetStoredAudioVideoSettings } from "../core/hooks/useStoredAudioVideoSettings";
import { DataChannelMessage } from "../core/message/types";
import { formatDataChannelMessage } from "../core/message/utils";
import { videoSDKMutex } from "../core/mutex";
import {
  localAudioContextAtom,
  localMediaStatusesAtom,
  localVideoContextAtom,
} from "../core/room/participant/media/atoms";
import { RoomDelegator } from "../core/room/RoomDelegator";
import type {
  AudioVideoSettings,
  BackgroundSettings,
  ConnectLocalAudioVideo,
  LocalAudioContextAtom,
  LocalAudioContextSetter,
  LocalScreenShareContextAtom,
  LocalScreenShareContextSetter,
  LocalVideoContextAtom,
  LocalVideoContextSetter,
  PublishMediaOptions,
  RecordingActionResult,
} from "../core/types";
import {
  ChangeCameraStatusCode,
  LocalMediaStatus,
  SetCameraResult,
} from "../core/types";
import {
  GenericRoomError,
  getStoredAudioVideoSettings,
  RoomValidationError,
  setStoredAudioVideoSettings,
  validateRoom,
} from "../core/utils";
import { ZoomLocalVideo } from "./components/ZoomLocalVideo";
import { ZoomRemoteAudioVideo } from "./components/ZoomRemoteAudioVideo";
import {
  ZoomLocalScreenShare,
  ZoomRemoteScreenShare,
} from "./components/ZoomScreenShare";
import { ZoomVideoContainer } from "./components/ZoomVideoContainer";
import { ZOOM_COMMAND_CHANNEL_MESSAGE_MAX_SIZE_BYTES } from "./constants";
import { ZoomLocalAudioDelegator } from "./room/participant/media/LocalAudioDelegator";
import { ZoomLocalPreviewAudioDelegator } from "./room/participant/media/LocalPreviewAudioDelegator";
import { ZoomLocalPreviewVideoDelegator } from "./room/participant/media/LocalPreviewVideoDelegator";
import { ZoomLocalScreenShareDelegator } from "./room/participant/media/LocalScreenShareDelegator";
import { ZoomLocalVideoDelegator } from "./room/participant/media/LocalVideoDelegator";
import { ZoomRoom } from "./room/RoomDelegator";
import { StartVideoArgs, ZoomErrorCode } from "./types";
import type { VideoClient as ZoomVideoClient } from "@zoom/videosdk";
import { isZoomExecutedFailure, ZoomExecutedFailureError } from "./utils/error";
import { handleError } from "./utils/handleError";
import {
  getVirtualBackgroundArg,
  performZoomAudioOperation,
  performZoomVideoOperation,
} from "./utils/media";
import {
  initializePreviewAudio,
  initializePreviewAudioVideo,
  initializePreviewVideo,
} from "./utils/preview";
import { parseZoomToken } from "./utils/token";
import { ZoomEventBus } from "./ZoomEventBus";
import {
  closeSessionInteractionPrompt,
  showSessionInteractionPrompt,
} from "./utils/SessionInteractionPrompt";
import { zoomTokenAtom } from "./state";
import { getZoomFailoverConfig } from "./utils/getZoomFailoverConfig";
import { userTypeAtom } from "../../auth/state";
import { UserType } from "../../types";
import { Statsig } from "../../statsig/StatsigProvider";
import { telehealthManualAdmissionFlowGate } from "../../statsig/gates";

type VideoClient = typeof ZoomVideoClient;

interface ConstructorArgs {
  videoClient: VideoClient;
  jotaiStore: JotaiStore;
}

const MAX_DELAY_BEFORE_JOINING_ROOM_MS = secondsToMilliseconds(3);
const MIN_DELAY_BEFORE_JOINING_ROOM_MS = 100;

/**
 * Zoom SDK operates in two modes:
 * 1. WebRTC mode: Uses browser's native WebRTC for video/audio processing
 * 2. WASM mode: Uses WebAssembly for processing
 *
 * We configure WebRTC mode via `video_webrtc_mode: 1` and `audio_webrtc_mode: 1` in the
 * Zoom token to maximize browser compatibility and avoid SharedArrayBuffer cross-origin
 * isolation requirements.
 *
 * Importantly, in WASM, features like virtual background and background noise
 * suppression require configuring the SharedArrayBuffer browser API.
 */
export class ZoomVideoSDKAdapter implements VideoSDKAdapter {
  #displayName: string = "";
  #jotaiStore: JotaiStore;
  #videoClient: VideoClient;
  #previewAudioDelegator: ZoomLocalPreviewAudioDelegator | null = null;
  #previewVideoDelegator: ZoomLocalPreviewVideoDelegator | null = null;
  #passivelyStopShareHandler: (() => void) | null = null;

  readonly VideoComponents = {
    Container: ZoomVideoContainer,
    LocalVideo: ZoomLocalVideo,
    RemoteAudioVideo: ZoomRemoteAudioVideo,
    LocalScreenShare: ZoomLocalScreenShare,
    RemoteScreenShare: ZoomRemoteScreenShare,
  };

  /**
   * Initialization requires an async operation and constructors cannot be async.
   */
  static async create({
    jotaiStore,
  }: Pick<ConstructorArgs, "jotaiStore">): Promise<ZoomVideoSDKAdapter> {
    const videoClient = ZoomVideoSDK.createClient();

    // TODO: Consider changing Global, the arg for getting the WebAssembly deps
    // patchJsMedia injects Zoom's necessary video-player WebComponents elements
    await videoClient.init("en-US", "Global", {
      patchJsMedia: true,
      // needed to support virtual backgrounds in preview video tracks
      enforceVirtualBackground: !window.crossOriginIsolated,
      // https://devforum.zoom.us/t/subscribe-video-counts-greater-than-maximum-size-4/113916
      enforceMultipleVideos: {
        disableRenderLimits: true,
      },
    });
    // initialize the Event Bus singleton
    ZoomEventBus.initialize(videoClient);

    return new ZoomVideoSDKAdapter({
      videoClient,
      jotaiStore,
    });
  }

  // Lifecycle

  private constructor({ videoClient, jotaiStore }: ConstructorArgs) {
    this.#videoClient = videoClient;
    this.#jotaiStore = jotaiStore;
  }

  async joinRoom(
    token: string,
    displayName: string,
    _localAudioContext: LocalAudioContextAtom,
    _localVideoContext: LocalVideoContextAtom,
  ): Promise<RoomDelegator> {
    this.#displayName = displayName;

    const prevMediaStatuses = await videoSDKMutex.runExclusive(() => {
      const prev = this.#jotaiStore.get(localMediaStatusesAtom);
      this.#jotaiStore.set(localMediaStatusesAtom, {
        audio: LocalMediaStatus.LOADING,
        video: LocalMediaStatus.LOADING,
      });
      return prev;
    });

    // Small delay to ensure reactive updates have a chance to run e.g. video
    // detach
    await wait(100);

    return videoSDKMutex.runExclusive(async () => {
      try {
        await this.#gracefullyCleanUpPreviews();
        // Another delay to ensure Zoom SDK has a chance to clean, just in case
        await wait(this.joinRoomDelayMs);

        return await this.#joinZoomRoom(token, displayName);
      } catch (error) {
        if (
          isZoomExecutedFailure(error) &&
          error.errorCode === ZoomErrorCode.CONNECTION_ERROR
        ) {
          throw new GenericRoomError({
            kind: "connection-error",
            message: error.reason,
            code: error.errorCode,
          });
        }
        this.#jotaiStore.set(localMediaStatusesAtom, prevMediaStatuses);
        throw error;
      } finally {
        // Side-effect: use the audio output device if one was previously set
        const audioOutputDeviceId = this.#jotaiStore.get(
          currentOutputDeviceIdAtom,
        );
        if (audioOutputDeviceId) {
          this.switchAudioOutput({
            deviceId: audioOutputDeviceId,
          });
        }

        // Stop loading and reflect current state
        this.#jotaiStore.set(localMediaStatusesAtom, {
          audio: LocalMediaStatus.DISABLED,
          video: LocalMediaStatus.DISABLED,
        });

        // Connect Zoom media streams or reinitialize the local ones. This can
        // take a bit and joining the room shouldn't be blocked on it.
        this.connectAudioVideoInputs({
          getLocalAudioContext: () =>
            this.#jotaiStore.get(localAudioContextAtom),
          setLocalAudioContext: (updater) =>
            this.#jotaiStore.set(localAudioContextAtom, updater),
          getLocalVideoContext: () =>
            this.#jotaiStore.get(localVideoContextAtom),
          setLocalVideoContext: (updater) =>
            this.#jotaiStore.set(localVideoContextAtom, updater),
          setHasBackgroundProcessorFailure: (_value: boolean) => {
            // TODO: TREE-123
          },
          setStoredAudioVideoSettings,
          disableSessionAudioVideo:
            !Statsig.checkGate(telehealthManualAdmissionFlowGate) &&
            this.#jotaiStore.get(userTypeAtom) === UserType.CLIENT,
        }).catch((error: Error) => {
          handleError(
            "[ZoomVideoSDKAdapter]: join, failed to connect audio/video after joining",
            error,
          );
        });
      }
    });
  }

  async #joinZoomRoom(token: string, displayName: string) {
    const tokenPayload = parseZoomToken(token);
    if (tokenPayload === null) {
      throw new Error("Failed to parse Zoom token, unable to join room.");
    }
    const { tpc: roomName } = tokenPayload;

    return (
      this.#videoClient
        // Zoom's type definition says join() has ExecutedResult return type but that's wrong
        .join(roomName, token, displayName)
        .then(async () => {
          const roomDelegator = new ZoomRoom(this.#videoClient);
          await validateRoom(roomDelegator).catch(
            async (error: RoomValidationError) => {
              await roomDelegator.disconnect().finally(() => {
                throw error;
              });
            },
          );
          return roomDelegator;
        })
    );
  }

  async leaveRoom(
    ...args: Parameters<VideoSDKAdapter["leaveRoom"]>
  ): ReturnType<VideoSDKAdapter["leaveRoom"]> {
    return videoSDKMutex.runExclusive(() => this.#leaveRoom(...args));
  }

  /**
   * When a user loses connection and the Zoom SDK cannot automatically
   * reconnect, then the user is in a failover state.
   */
  async #handleFailover(): Promise<void> {
    logger.warn("[ZoomVideoSDKAdapter]: handling failover");

    const roomToken = this.#jotaiStore.get(zoomTokenAtom);
    if (!roomToken) {
      throw new Error(
        "[ZoomVideoSDKAdapter]: No room token available for rejoining before leaving gracefully.",
      );
    }

    const { joinRetries, joinRetryDelayMs } = getZoomFailoverConfig();
    await withRetry(() => this.#joinZoomRoom(roomToken, this.#displayName), {
      retries: joinRetries,
      initialDelayMs: joinRetryDelayMs,
    })();
  }

  async #leaveRoom({
    shouldEndRoom,
    connectionError,
  }: {
    shouldEndRoom?: boolean;
    connectionError?: Error;
  } = {}): Promise<ZoomRoom> {
    const isInFailover =
      this.#videoClient.getCurrentUserInfo()?.isInFailover || connectionError;
    if (isInFailover) {
      await this.#handleFailover();
    }

    if (!this.userIsInSession) {
      return new ZoomRoom(this.#videoClient);
    }

    // Loading state prevents element attachment when leaving
    return await withTemporaryState({
      operation: async () => {
        await this.stream.stopAudio().catch((error: Error) => {
          handleError(
            "[ZoomVideoSDKAdapter]: leave, stop audio failure",
            error,
          );
        });
        await this.stream.stopVideo().catch((error: Error) => {
          handleError(
            "[ZoomVideoSDKAdapter]: leave, stop video failure",
            error,
          );
        });

        // Failure to leave will and should bubble up
        await this.#videoClient.leave(shouldEndRoom);

        // It's important to clear the room before reconnecting the preview
        // video in order for component attach/detach logic to work correctly
        // between preview and in-session local video
        this.#jotaiStore.set(videoSDKAtom, (prev) => ({
          ...prev,
          room: undefined,
          participants: [],
        }));

        // Reconnect previews
        await this.#connectAudioVideoInputs({
          getLocalAudioContext: () =>
            this.#jotaiStore.get(localAudioContextAtom),
          setLocalAudioContext: (updater) =>
            this.#jotaiStore.set(localAudioContextAtom, updater),
          getLocalVideoContext: () =>
            this.#jotaiStore.get(localVideoContextAtom),
          setLocalVideoContext: (updater) =>
            this.#jotaiStore.set(localVideoContextAtom, updater),
          setHasBackgroundProcessorFailure: (_value: boolean) => {
            // TODO: TREE-123
          },
          setStoredAudioVideoSettings,
        }).catch((error: Error) => {
          handleError(
            "[ZoomVideoSDKAdapter]: leave, failed to initialize preview audio/video after leaving",
            error,
          );
        });

        return new ZoomRoom(this.#videoClient);
      },
      jotaiStore: this.#jotaiStore,
      atom: localMediaStatusesAtom,
      value: {
        audio: LocalMediaStatus.LOADING,
        video: LocalMediaStatus.LOADING,
      },
      onConflict: (currentValue, previousValue) => ({
        audio:
          currentValue.audio === LocalMediaStatus.LOADING
            ? previousValue.audio
            : currentValue.audio,
        video:
          currentValue.video === LocalMediaStatus.LOADING
            ? previousValue.video
            : currentValue.video,
      }),
    });
  }

  async removeParticipant(participantSid: string): Promise<void> {
    await this.#videoClient.removeUser(parseInt(participantSid));
  }

  /**
   * This interface method is invoked at two key points:
   * - pre-session, following adapter initialized
   * - in-session, immediately after `joinRoom` is called
   *
   * Unlike Twilio, Zoom distinguishes between *pre-session* and *in-session*
   * states for audio and video handling.
   *
   * See the higher level `useConnectAudioVideoInputs` for more details.
   */
  async connectAudioVideoInputs(props: ConnectLocalAudioVideo): Promise<void> {
    return await videoSDKMutex.runExclusive(async () =>
      this.#connectAudioVideoInputs(props),
    );
  }

  async #connectAudioVideoInputs(props: ConnectLocalAudioVideo): Promise<void> {
    const {
      getLocalAudioContext,
      setLocalAudioContext,
      getLocalVideoContext,
      setLocalVideoContext,
      setStoredAudioVideoSettings,
      disableSessionAudioVideo,
    } = props;

    const sessionAudioVideoIsDisabled =
      this.userIsInSession && disableSessionAudioVideo;

    await withTemporaryState({
      operation: async () => {
        const { inputAudioDeviceId, isAudioEnabled, isVideoEnabled } =
          getStoredAudioVideoSettings();

        if (!this.userIsInSession) {
          const { success, previewAudioDelegator, previewVideoDelegator } =
            await initializePreviewAudioVideo(
              this.#videoClient,
              this.isVirtualBackgroundSupported,
            );

          if (!success) {
            // permission dismissed/cancelled - set contexts to DISABLED state
            setLocalAudioContext({ status: LocalMediaStatus.DISABLED });
            setLocalVideoContext({ status: LocalMediaStatus.DISABLED });
            return;
          }

          this.#previewAudioDelegator = previewAudioDelegator;
          this.#previewVideoDelegator = previewVideoDelegator;

          // sets the preview delegators even if they are disabled/muted which
          // allows background changing to enable
          if (previewAudioDelegator) {
            this.#jotaiStore.set(localAudioContextAtom, (prev) => ({
              ...prev,
              delegator: previewAudioDelegator,
            }));
          }
          if (previewVideoDelegator) {
            this.#jotaiStore.set(localVideoContextAtom, (prev) => ({
              ...prev,
              delegator: previewVideoDelegator,
            }));
          }
        } else {
          await this.#gracefullyCleanUpPreviews();

          if (!disableSessionAudioVideo) {
            await this.startSessionAudio({
              deviceId: inputAudioDeviceId,
              isEnabled: isAudioEnabled,
            })
              .catch((error) => {
                // Allows user to manually retry enabling audio/video
                this.#jotaiStore.set(localMediaStatusesAtom, {
                  audio: LocalMediaStatus.DISABLED,
                  video: LocalMediaStatus.DISABLED,
                });
                throw error;
              })
              .then(async () => {
                // TODO: TREE-239 Enable based on stored setting
                await this.stream
                  .enableBackgroundNoiseSuppression(true)
                  .catch((error: ExecutedFailure) => {
                    logger.warn(
                      "[ZoomVideoSDKAdapter]: Failed to enable background noise suppression",
                      {},
                      new ZoomExecutedFailureError(error),
                    );
                  });
              });
          }
        }

        if (isAudioEnabled && !sessionAudioVideoIsDisabled) {
          await this.#unmuteAudio(
            getLocalAudioContext,
            setLocalAudioContext,
            setStoredAudioVideoSettings,
          );
        }

        if (isVideoEnabled && !sessionAudioVideoIsDisabled) {
          await this.#startVideo(
            getLocalVideoContext,
            setLocalVideoContext,
            setStoredAudioVideoSettings,
            {},
          );
        }
      },
      jotaiStore: this.#jotaiStore,
      atom: localMediaStatusesAtom,
      value: {
        audio: LocalMediaStatus.LOADING,
        video: LocalMediaStatus.LOADING,
      },
      onConflict: (currentValue, previousValue) => {
        return {
          audio:
            currentValue.audio === LocalMediaStatus.LOADING
              ? previousValue.audio
              : currentValue.audio,
          video:
            currentValue.video === LocalMediaStatus.LOADING
              ? previousValue.video
              : currentValue.video,
        };
      },
    });

    if (sessionAudioVideoIsDisabled) {
      setLocalAudioContext({ status: LocalMediaStatus.DISABLED });
      setLocalVideoContext({ status: LocalMediaStatus.DISABLED });

      showSessionInteractionPrompt();
    }
  }

  // Audio

  async switchAudioOutput(
    ...args: Parameters<VideoSDKAdapter["switchAudioOutput"]>
  ): ReturnType<VideoSDKAdapter["switchAudioOutput"]> {
    await videoSDKMutex.runExclusive(() => this.#switchAudioOutput(...args));
  }

  async #switchAudioOutput({ deviceId }: { deviceId: string }): Promise<void> {
    if (this.userIsInSession) {
      await this.stream.switchSpeaker(deviceId);
    }
    this.#jotaiStore.set(currentOutputDeviceIdAtom, deviceId);
  }

  async unmuteAudio(
    ...args: Parameters<VideoSDKAdapter["unmuteAudio"]>
  ): ReturnType<VideoSDKAdapter["unmuteAudio"]> {
    return await videoSDKMutex.runExclusive(() => this.#unmuteAudio(...args));
  }

  async #unmuteAudio(
    _: () => LocalAudioContextAtom,
    setLocalAudioContext: LocalAudioContextSetter,
    _setStoredAudioVideoSettings: SetStoredAudioVideoSettings,
  ): Promise<boolean> {
    const successStatus = LocalMediaStatus.READY;

    if (!this.userIsInSession) {
      // handle case when user dismisses initial permission prompt
      if (!this.#previewAudioDelegator) {
        const previewAudioDelegator = await initializePreviewAudio();
        if (!previewAudioDelegator) {
          setLocalAudioContext({ status: LocalMediaStatus.ERROR });
          return false;
        }
        this.#previewAudioDelegator = previewAudioDelegator;
      }

      return performZoomAudioOperation({
        setLocalAudioContext,
        operation: async () => {
          if (this.previewAudioDelegator.track.isMicUnmuted === false) {
            await this.previewAudioDelegator.track.unmute();
          }
        },
        getDelegator: () => this.previewAudioDelegator,
        successStatus,
        errorContext: "[ZoomVideoSDKAdapter]: unmute preview audio",
      });
    }

    return performZoomAudioOperation({
      setLocalAudioContext,
      operation: async () => {
        const { inputAudioDeviceId } = getStoredAudioVideoSettings();
        const userHasNotJoinedAudio =
          this.#videoClient.getCurrentUserInfo()?.muted === undefined;
        if (userHasNotJoinedAudio) {
          await this.startSessionAudio({
            deviceId: inputAudioDeviceId,
            isEnabled: true,
          });
          return;
        }
        await this.stream.unmuteAudio();
      },
      getDelegator: () => new ZoomLocalAudioDelegator(this.stream),
      successStatus,
      errorContext: "[ZoomVideoSDKAdapter]: unmute session audio",
    });
  }

  async muteAudio(
    ...args: Parameters<VideoSDKAdapter["muteAudio"]>
  ): ReturnType<VideoSDKAdapter["muteAudio"]> {
    return await videoSDKMutex.runExclusive(() => this.#muteAudio(...args));
  }

  async #muteAudio(
    _: () => LocalAudioContextAtom,
    setLocalAudioContext: LocalAudioContextSetter,
    _shouldReleaseTrack?: boolean,
  ): Promise<boolean> {
    const successStatus = LocalMediaStatus.DISABLED;

    if (!this.userIsInSession) {
      return performZoomAudioOperation({
        setLocalAudioContext,
        operation: async () => {
          if (this.previewAudioDelegator.track.isMicUnmuted) {
            await this.previewAudioDelegator.track.mute();
          }
        },
        getDelegator: () => this.previewAudioDelegator,
        successStatus,
        errorContext: "[ZoomVideoSDKAdapter]: mute preview audio",
      });
    }

    return performZoomAudioOperation({
      setLocalAudioContext,
      operation: () => this.stream.muteAudio(),
      getDelegator: () => new ZoomLocalAudioDelegator(this.stream),
      successStatus,
      errorContext: "[ZoomVideoSDKAdapter]: mute session audio",
    });
  }

  async switchMicrophone(
    ...args: Parameters<VideoSDKAdapter["switchMicrophone"]>
  ): ReturnType<VideoSDKAdapter["switchMicrophone"]> {
    return await videoSDKMutex.runExclusive(() =>
      this.#switchMicrophone(...args),
    );
  }

  async #switchMicrophone(
    deviceId: string,
    _: () => LocalAudioContextAtom,
    setLocalAudioContext: LocalAudioContextSetter,
    setStoredAudioVideoSettings: SetStoredAudioVideoSettings,
  ): Promise<boolean> {
    if (!this.userIsInSession) {
      const switchPreviewMicrophone = async () => {
        // release current device and assign an enabled device
        await this.#gracefullyCleanUpPreviewAudioDelegator();
        this.#previewAudioDelegator =
          await ZoomLocalPreviewAudioDelegator.create(deviceId, true);
      };

      const success = await performZoomAudioOperation({
        setLocalAudioContext,
        operation: switchPreviewMicrophone,
        getDelegator: () => this.previewAudioDelegator,
        successStatus: LocalMediaStatus.READY,
        errorContext: "[ZoomVideoSDKAdapter]: switch preview microphone",
      });

      if (success) {
        setStoredAudioVideoSettings((prevSettings) => ({
          ...prevSettings,
          inputAudioDeviceId: deviceId,
        }));
      }

      return success;
    }

    const success = await performZoomAudioOperation({
      setLocalAudioContext,
      operation: async () => {
        const result = await this.stream.switchMicrophone(deviceId);

        // always unmute after switching
        await this.#unmuteAudio(
          () => this.#jotaiStore.get(localAudioContextAtom),
          setLocalAudioContext,
          setStoredAudioVideoSettings,
        );

        return result;
      },
      getDelegator: () => new ZoomLocalAudioDelegator(this.stream),
      successStatus: LocalMediaStatus.READY,
      errorContext: "[ZoomVideoSDKAdapter]: switch session microphone",
    });

    if (success) {
      setStoredAudioVideoSettings((prevSettings) => ({
        ...prevSettings,
        inputAudioDeviceId: deviceId,
      }));
    }

    return success;
  }

  // Video

  async startVideo(
    ...args: Parameters<VideoSDKAdapter["startVideo"]>
  ): ReturnType<VideoSDKAdapter["startVideo"]> {
    return await videoSDKMutex.runExclusive(() => this.#startVideo(...args));
  }

  async #startVideo(
    _localVideoContext: () => LocalVideoContextAtom,
    setLocalVideoContext: LocalVideoContextSetter,
    _setStoredAudioVideoSettings: SetStoredAudioVideoSettings,
    { backgroundOptions }: PublishMediaOptions<unknown, unknown> = {},
  ): Promise<boolean> {
    const successStatus = LocalMediaStatus.READY;

    const { backgroundType, backgroundIndex } =
      backgroundOptions ?? getStoredAudioVideoSettings();
    const virtualBackground = await getVirtualBackgroundArg(
      backgroundType,
      backgroundIndex,
      this.isVirtualBackgroundSupported,
    );
    if (!this.userIsInSession) {
      return this.startPreviewVideo({
        setLocalVideoContext,
        successStatus,
        ...virtualBackground,
      });
    }
    return this.startSessionVideo({
      setLocalVideoContext,
      successStatus,
      ...virtualBackground,
    });
  }

  async stopVideo(
    ...args: Parameters<VideoSDKAdapter["stopVideo"]>
  ): ReturnType<VideoSDKAdapter["stopVideo"]> {
    return await videoSDKMutex.runExclusive(() => this.#stopVideo(...args));
  }

  async #stopVideo(
    _: () => LocalVideoContextAtom,
    setLocalVideoContext: LocalVideoContextSetter,
    _shouldReleaseTrack?: boolean,
  ): Promise<boolean> {
    const successStatus = LocalMediaStatus.DISABLED;

    if (!this.userIsInSession) {
      return performZoomVideoOperation({
        setLocalVideoContext,
        operation: () => this.previewVideoDelegator.disable(),
        getDelegator: () => this.previewVideoDelegator,
        successStatus,
        errorContext: "[ZoomVideoSDKAdapter]: stop preview video",
      });
    }

    if (!this.localVideoIsEnabled) {
      return false;
    }

    const localParticipantUserId = this.localParticipantUserId;
    if (!localParticipantUserId) {
      handleError(
        "[ZoomVideoSDKAdapter]: stopVideo",
        new Error("Missing local participant user ID"),
      );
      return false;
    }

    return performZoomVideoOperation({
      setLocalVideoContext,
      operation: () => this.stream.stopVideo(),
      getDelegator: () =>
        new ZoomLocalVideoDelegator(localParticipantUserId, this.stream),
      successStatus,
      errorContext: "[ZoomVideoSDKAdapter]: stop session video",
    });
  }

  async switchCamera(
    ...args: Parameters<VideoSDKAdapter["switchCamera"]>
  ): ReturnType<VideoSDKAdapter["switchCamera"]> {
    return await videoSDKMutex.runExclusive(() => this.#switchCamera(...args));
  }

  async #switchCamera(
    cameraDeviceId: string,
    _localVideoContext: () => LocalVideoContextAtom,
    setLocalVideoContext: LocalVideoContextSetter,
    setStoredAudioVideoSettings: SetStoredAudioVideoSettings,
    _setHasBackgroundProcessorFailure: (value: boolean) => void,
    _localParticipant?: TwilioLocalParticipant,
  ): Promise<SetCameraResult> {
    const errorResult = {
      success: false,
      code: ChangeCameraStatusCode.ERROR,
    };
    const successStatus = LocalMediaStatus.READY;

    if (!this.userIsInSession) {
      const switchCameraResult = await performZoomVideoOperation({
        setLocalVideoContext,
        operation: () =>
          this.previewVideoDelegator.enable({
            deviceId: cameraDeviceId,
          }),
        getDelegator: () => this.previewVideoDelegator,
        successStatus,
        errorContext: "[ZoomVideoSDKAdapter]: switch preview video camera",
      });

      if (switchCameraResult === false) {
        return errorResult;
      }

      setStoredAudioVideoSettings((prevSettings) => ({
        ...prevSettings,
        inputVideoDeviceId: cameraDeviceId,
      }));

      return {
        success: true,
        code: ChangeCameraStatusCode.CHANGED,
      };
    }

    const localParticipantUserId = this.localParticipantUserId;
    if (!localParticipantUserId) {
      handleError(
        "[ZoomVideoSDKAdapter]: switchCamera",
        new Error("Missing local participant user ID"),
      );
      return errorResult;
    }

    const switchCameraResult = this.localVideoIsEnabled
      ? await performZoomVideoOperation({
          setLocalVideoContext,
          operation: () => this.stream.switchCamera(cameraDeviceId),
          getDelegator: () =>
            new ZoomLocalVideoDelegator(localParticipantUserId, this.stream),
          successStatus,
          errorContext: "[ZoomVideoSDKAdapter]: switch session video camera",
        })
      : await this.startSessionVideo({
          deviceId: cameraDeviceId,
          setLocalVideoContext,
          successStatus,
        });

    if (switchCameraResult === false) {
      return errorResult;
    }

    setStoredAudioVideoSettings((prevSettings) => ({
      ...prevSettings,
      inputVideoDeviceId: cameraDeviceId,
    }));

    return {
      success: true,
      code: ChangeCameraStatusCode.CHANGED,
    };
  }

  /**
   * Virtual backgrounds work differently across Zoom's two modes:
   * - WebRTC mode: Works without SharedArrayBuffer
   * - WASM mode: Requires SharedArrayBuffer enabled via COEP/COOP headers
   *
   * Known issues with Zoom's isSupportVirtualBackground():
   * - Depends on the element provided to stream.attachVideo()
   *   - <video-player> provides the most accurate information
   * - Requires browser's hardware acceleration enabled
   */
  async switchBackground(
    ...args: Parameters<VideoSDKAdapter["switchBackground"]>
  ): ReturnType<VideoSDKAdapter["switchBackground"]> {
    return await videoSDKMutex.runExclusive(() =>
      this.#switchBackground(...args),
    );
  }

  async #switchBackground(
    backgroundSettings: BackgroundSettings,
    getLocalVideoContext: () => LocalVideoContextAtom,
    setLocalVideoContext: LocalVideoContextSetter,
    setStoredAudioVideoSettings: (
      updater: (prev: AudioVideoSettings) => AudioVideoSettings,
    ) => void,
    setHasBackgroundProcessorFailure: (value: boolean) => void,
    {
      persistChanges = true,
    }: {
      persistChanges?: boolean;
    } = {},
    _localParticipant?: TwilioLocalParticipant,
  ): Promise<void> {
    if (!this.isVirtualBackgroundSupported) {
      logger.error("Virtual backgrounds are not supported on this device");
      return;
    }

    setLocalVideoContext((prev) => ({
      ...prev,
      status: LocalMediaStatus.LOADING,
    }));

    const {
      backgroundType: targetBackgroundType,
      backgroundIndex: targetBackgroundIndex,
    } = backgroundSettings;
    const virtualBackground = await getVirtualBackgroundArg(
      targetBackgroundType,
      targetBackgroundIndex,
      this.isVirtualBackgroundSupported,
    );
    const { virtualBackgroundType, virtualBackgroundValue } = virtualBackground;

    try {
      if (!this.userIsInSession) {
        await this.previewVideoDelegator?.enable(virtualBackground);
      } else {
        if (virtualBackgroundType) {
          await this.stream.updateVirtualBackgroundImage(
            virtualBackgroundValue && virtualBackgroundValue.imageUrl,
          );
        }

        // always start video if it was off
        const isCameraOff = !this.#videoClient.getCurrentUserInfo()?.bVideoOn;
        if (isCameraOff) {
          await this.#startVideo(
            getLocalVideoContext,
            setLocalVideoContext,
            setStoredAudioVideoSettings,
            {
              backgroundOptions: {
                backgroundType: targetBackgroundType,
                backgroundIndex: targetBackgroundIndex,
              },
            },
          );
        }
      }

      setHasBackgroundProcessorFailure(false);
      setLocalVideoContext((prev) => ({
        ...prev,
        status: LocalMediaStatus.READY,
      }));

      if (persistChanges) {
        setStoredAudioVideoSettings((prev) => ({
          ...prev,
          backgroundType: targetBackgroundType,
          backgroundIndex: targetBackgroundIndex,
        }));
      }
    } catch (error) {
      handleError(
        "[ZoomVideoSDKAdapter] Failed to update virtual background",
        error,
        {
          backgroundType: targetBackgroundType,
          backgroundIndex: targetBackgroundIndex,
        },
      );
      setHasBackgroundProcessorFailure(true);
      setLocalVideoContext((prev) => ({
        ...prev,
        status: LocalMediaStatus.ERROR,
      }));
    }
  }

  get isVirtualBackgroundSupported(): boolean {
    return this.stream.isSupportVirtualBackground();
  }

  async startScreenShare(
    setLocalScreenShareContext: LocalScreenShareContextSetter,
    onError?: (error: Error) => void,
  ): Promise<boolean> {
    // one-off event handler, doesn't fit in Room or RemoteParticipant
    this.#passivelyStopShareHandler = () => {
      delegator.stop();
      setLocalScreenShareContext({ status: LocalMediaStatus.DISABLED });
    };

    this.#videoClient.on(
      "passively-stop-share",
      this.#passivelyStopShareHandler,
    );

    const handleShareScreenSuccess = () => {
      setLocalScreenShareContext((previous) => ({
        ...previous,
        status: LocalMediaStatus.READY,
      }));
    };

    const handleShareScreenError = (error: Error) => {
      onError?.(error);
      handleError("[ZoomVideoSDKAdapter]: startScreenShare", error);
      // Clean up event handler on failure
      if (this.#passivelyStopShareHandler) {
        this.#videoClient.off(
          "passively-stop-share",
          this.#passivelyStopShareHandler,
        );
        this.#passivelyStopShareHandler = null;
      }
      setLocalScreenShareContext((previous) => ({
        ...previous,
        status: LocalMediaStatus.ERROR,
      }));
    };

    const delegator = new ZoomLocalScreenShareDelegator(
      this.stream,
      handleShareScreenSuccess,
      handleShareScreenError,
    );

    // Zoom's delegator start only sets the loading state
    setLocalScreenShareContext({
      delegator: delegator,
      status: LocalMediaStatus.LOADING,
    });

    return true;
  }

  async stopScreenShare(
    getLocalScreenShareContext: () => LocalScreenShareContextAtom,
    setLocalScreenShareContext: LocalScreenShareContextSetter,
  ): Promise<boolean> {
    const { delegator } = getLocalScreenShareContext();
    if (!delegator) {
      return false;
    }

    if (!this.#passivelyStopShareHandler) {
      return false;
    }

    try {
      await delegator.stop();
      // unset the delegator
      setLocalScreenShareContext({ status: LocalMediaStatus.DISABLED });

      // de-register our one-off
      this.#videoClient.off(
        "passively-stop-share",
        this.#passivelyStopShareHandler,
      );

      return true;
    } catch (error) {
      handleError("[ZoomVideoSDKAdapter]: stopScreenShare", error);
      setLocalScreenShareContext((previous) => ({
        ...previous,
        status: LocalMediaStatus.ERROR,
      }));

      return false;
    }
  }

  // Messages

  /**
   * Zoom's CommandChannel has a 512 character string limit.
   */
  async sendMessage(message: DataChannelMessage): Promise<boolean> {
    if (!this.userIsInSession) {
      logger.warn("Failed to send message, user is not in session");
      return false;
    }

    const dataChannel = this.#videoClient.getCommandClient();

    // always compress
    const compressedMessage = await formatDataChannelMessage(
      message,
      ZOOM_COMMAND_CHANNEL_MESSAGE_MAX_SIZE_BYTES,
    );

    const messageContext = {
      messageType: message.type,
      messageSender: message.sender,
    };

    if (!compressedMessage) {
      logger.warn(
        "Failed to send message, exceeds Zoom's size limit",
        messageContext,
      );
      return false;
    }

    try {
      await dataChannel.send(compressedMessage);
      return true;
    } catch (error) {
      if (
        isZoomExecutedFailure(error) &&
        error.errorCode === ZoomErrorCode.CMD_CHANNEL_NOT_READY
      ) {
        // The command channel is sometimes not ready right after a user
        // reconnects. There doesn't seem to a way to await it other than
        // listening for an event:
        // https://devforum.zoom.us/t/getting-error-while-sending-command-type-improper-meeting-state-reason-cmd-channel-not-ready/67002/4
        logger.warn(
          "[ZoomVideoSDKAdapter]: Failed to send message, command channel not ready yet",
        );
        return false;
      }
      handleError("[ZoomVideoSDKAdapter]: sendMessage", error, messageContext);
      return false;
    }
  }

  /**
   * Does not use the injected callback dependency.
   */
  async startAudioRecording(
    ...args: Parameters<VideoSDKAdapter["startAudioRecording"]>
  ): ReturnType<VideoSDKAdapter["startAudioRecording"]> {
    return await videoSDKMutex.runExclusive(() =>
      this.#startAudioRecording(...args),
    );
  }

  async #startAudioRecording(
    _handleStartRecording: () => Promise<void>,
  ): Promise<RecordingActionResult> {
    const recordingClient = this.#videoClient.getRecordingClient();
    const recordingStatus = recordingClient.getCloudRecordingStatus();
    try {
      if (recordingStatus === RecordingStatus.Paused) {
        await recordingClient.resumeCloudRecording();
      } else {
        await recordingClient.startCloudRecording();
      }
    } catch (error) {
      handleError("[ZoomVideoSDKAdapter]: startAudioRecording", error);
      return { success: false, error: normalizeError(error) };
    }

    return { success: true, error: null };
  }

  async stopAudioRecording(
    ...args: Parameters<VideoSDKAdapter["stopAudioRecording"]>
  ): ReturnType<VideoSDKAdapter["stopAudioRecording"]> {
    return await videoSDKMutex.runExclusive(() =>
      this.#stopAudioRecording(...args),
    );
  }

  async #stopAudioRecording(
    _handleStopRecording: () => Promise<void>,
  ): Promise<RecordingActionResult> {
    // no-op if not in session
    if (!this.userIsInSession) {
      return { success: true, error: null };
    }

    const recordingClient = this.#videoClient.getRecordingClient();

    try {
      await recordingClient.pauseCloudRecording();
    } catch (error) {
      handleError("[ZoomVideoSDKAdapter]: stopAudioRecording", error);
      return { success: false, error: normalizeError(error) };
    }

    return { success: true, error: null };
  }

  private async startPreviewVideo({
    setLocalVideoContext,
    successStatus,
    ...virtualBackground
  }: StartVideoArgs) {
    if (!this.#previewVideoDelegator) {
      const previewVideoDelegator = await initializePreviewVideo({
        videoClient: this.#videoClient,
        isVirtualBackgroundSupported: this.isVirtualBackgroundSupported,
      });
      if (!previewVideoDelegator) {
        setLocalVideoContext({ status: LocalMediaStatus.ERROR });
        return false;
      }
      this.#previewVideoDelegator = previewVideoDelegator;
    }

    return performZoomVideoOperation({
      setLocalVideoContext,
      operation: () => this.previewVideoDelegator.enable(virtualBackground),
      getDelegator: () => this.previewVideoDelegator,
      successStatus,
      errorContext: "[ZoomVideoSDKAdapter]: start preview video",
    });
  }

  /**
   * Starts the session audio (microphone). Use this instead of directly calling
   * `stream.startAudio` to avoid awaiting a promise that may never resolve in
   * some circumstances.
   */
  private async startSessionAudio({
    deviceId,
    isEnabled,
  }: {
    deviceId?: string;
    isEnabled?: boolean;
  } = {}) {
    await withTimeout(
      () =>
        this.stream.startAudio({
          microphoneId: deviceId,
          mute: !isEnabled,
        }),
      secondsToMilliseconds(30),
      {
        timeoutMessage: "[ZoomVideoSDKAdapter]: startSessionAudio timed out",
      },
    ).finally(closeSessionInteractionPrompt);
  }

  /**
   * Starts the session video (camera). Use this instead of directly calling
   * `stream.startVideo` to avoid awaiting a promise that may never resolve in
   * some circumstances.
   */
  private async startSessionVideo({
    setLocalVideoContext,
    successStatus,
    deviceId,
    ...virtualBackground
  }: StartVideoArgs & { deviceId?: string }) {
    const localParticipantUserId = this.localParticipantUserId;
    if (!localParticipantUserId) {
      handleError(
        "[ZoomVideoSDKAdapter]: startVideo",
        new Error("Missing local participant user ID"),
      );
      return false;
    }

    if (this.localVideoIsEnabled) {
      return false;
    }

    return performZoomVideoOperation({
      setLocalVideoContext,
      operation: async () =>
        withTimeout(
          async () => {
            const {
              inputVideoDeviceId: storedDeviceId,
              backgroundType,
              backgroundIndex,
            } = getStoredAudioVideoSettings();
            const { virtualBackgroundType, virtualBackgroundValue } =
              virtualBackground.virtualBackgroundType
                ? virtualBackground
                : await getVirtualBackgroundArg(
                    backgroundType,
                    backgroundIndex,
                    this.isVirtualBackgroundSupported,
                  );
            return this.stream.startVideo({
              cameraId: deviceId ?? storedDeviceId,
              ...(virtualBackgroundType
                ? { virtualBackground: virtualBackgroundValue }
                : {}),
            });
          },
          secondsToMilliseconds(30),
          {
            timeoutMessage:
              "[ZoomVideoSDKAdapter]: startSessionVideo timed out",
          },
        ),
      getDelegator: () =>
        new ZoomLocalVideoDelegator(localParticipantUserId, this.stream),
      successStatus,
      errorContext: "[ZoomVideoSDKAdapter]: start session video",
    });
  }

  async #gracefullyCleanUpPreviewAudioDelegator() {
    if (this.#previewAudioDelegator && this.#previewAudioDelegator.isEnabled) {
      await this.#previewAudioDelegator.track.stop().catch((error) => {
        handleError(
          "[ZoomVideoSDKAdapter]: cleanUpPreviewAudioDelegator, stop preview audio failure",
          error,
        );
      });
    }
    this.#previewAudioDelegator = null;
  }

  async #gracefullyCleanUpPreviewVideoDelegator() {
    if (this.#previewVideoDelegator && this.#previewVideoDelegator.isEnabled) {
      await this.#previewVideoDelegator.disable().catch((error) => {
        handleError(
          "[ZoomVideoSDKAdapter]: cleanUpPreviewVideoDelegator, disable preview video failure",
          error,
        );
      });
    }
    this.#previewVideoDelegator = null;
  }

  async #gracefullyCleanUpPreviews() {
    await Promise.allSettled([
      this.#gracefullyCleanUpPreviewAudioDelegator(),
      this.#gracefullyCleanUpPreviewVideoDelegator(),
    ]);
  }

  private get previewAudioDelegator(): ZoomLocalPreviewAudioDelegator {
    if (!this.#previewAudioDelegator) {
      throw new Error("Preview audio delegator not initialized");
    }
    return this.#previewAudioDelegator;
  }

  private get previewVideoDelegator(): ZoomLocalPreviewVideoDelegator {
    if (!this.#previewVideoDelegator) {
      throw new Error("Preview video delegator not initialized");
    }
    return this.#previewVideoDelegator;
  }

  private get stream() {
    return this.#videoClient.getMediaStream();
  }

  private get localParticipantUserId() {
    return this.#videoClient.getCurrentUserInfo()?.userId;
  }

  private get localVideoIsEnabled() {
    return this.#videoClient.getCurrentUserInfo()?.bVideoOn ?? false;
  }

  private get userIsInSession() {
    return this.#videoClient.getSessionInfo().isInMeeting;
  }

  /**
   * This delay before joining the room is recommended by Zoom to allow the
   * preview-track logic to clean up and avoid never-resolving promises when
   * attempting to start tracks in the session
   */
  private get joinRoomDelayMs() {
    const currentTimeMs = Date.now();
    const timeSincePreviewVideoLastStartedMs =
      currentTimeMs -
      (this.#previewVideoDelegator?.lastStartedAt ?? currentTimeMs);
    return Math.max(
      MIN_DELAY_BEFORE_JOINING_ROOM_MS,
      MAX_DELAY_BEFORE_JOINING_ROOM_MS - timeSincePreviewVideoLastStartedMs,
    );
  }
}
